import './home.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import api from '../api';

function toPublicUrl(baseUrl, storedUrl) {
  const p = String(storedUrl || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(baseUrl || '').replace(/\/$/, '');
  const rel = p.startsWith('/') ? p : `/${p}`;
  return base + rel;
}

function monthLabel(year, month) {
  const d = new Date(Date.UTC(year, month - 1, 1));
  try {
    return d.toLocaleString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  } catch {
    return `${String(month).padStart(2, '0')}.${year}`;
  }
}

export default function APNClipsTopMonth() {
  const { user } = useContext(UserContext);
  const baseUrl = useMemo(() => (import.meta.env.VITE_API_URL || 'http://localhost:5000'), []);

  const [months, setMonths] = useState([]);
  const [selected, setSelected] = useState(null); // {year, month}
  const [items, setItems] = useState([]);
  const [loadingMonths, setLoadingMonths] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: 16,
  };

  const loadMonths = async () => {
    setLoadingMonths(true);
    setError('');
    try {
      const res = await api.get('/apn-clips/top/months');
      const list = Array.isArray(res.data?.months) ? res.data.months : [];
      setMonths(list);
      setSelected(null);
    } catch (e) {
      setError(e?.response?.data?.error || 'Konnte Monatsliste nicht laden.');
    } finally {
      setLoadingMonths(false);
    }
  };

  const loadTop = async (year, month) => {
    if (!year || !month) return;
    setLoadingItems(true);
    setError('');
    try {
      const res = await api.get(`/apn-clips/top/${year}/${month}`);
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list);
    } catch (e) {
      setItems([]);
      setError(e?.response?.data?.error || 'Konnte Top-Clips nicht laden.');
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    loadMonths();
  }, []);

  useEffect(() => {
    if (selected?.year && selected?.month) {
      loadTop(selected.year, selected.month);
    } else {
      setItems([]);
    }
  }, [selected?.year, selected?.month]);

  const openItem = useMemo(() => items.find((x) => x.id === openId) || null, [items, openId]);
  const openVideoUrl = useMemo(() => (openItem ? toPublicUrl(baseUrl, openItem.storedUrl) : ''), [openItem, baseUrl]);

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Top Clips des Monats</h2>
        <p className="hero-desc" style={{ margin: '0 auto 18px', textAlign: 'center', maxWidth: 920 }}>
          Die 5 bestbewerteten Clips (👍 +3, 😐 +1, 👎 -2) pro abgeschlossenem Monat.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', margin: '6px 0 14px' }}>
          <Link className="hero-btn-secondary" to="/apn-clips" style={{ textDecoration: 'none' }}>Zurück</Link>
          <button className="hero-btn-primary" type="button" onClick={loadMonths} disabled={loadingMonths}>Neu laden</button>
        </div>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Monate</h2>

          {error && <div style={{ padding: '0 8px 12px', color: '#ffdddd', fontSize: 13 }}>{error}</div>}

          {loadingMonths ? (
            <div style={{ padding: '0 8px 10px', opacity: 0.8 }}>Lade…</div>
          ) : months.length === 0 ? (
            <div style={{ padding: '0 8px 10px', opacity: 0.85, fontSize: 14, lineHeight: 1.55 }}>
              Aktuell ist noch kein abgeschlossener Monat verfügbar. Ein Monat wird erst ab dem 1. des Folgemonats angezeigt.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '0 8px 12px' }}>
              {months.map((m) => {
                const isActive = selected?.year === m.year && selected?.month === m.month;
                return (
                  <button
                    key={`${m.year}-${m.month}`}
                    type="button"
                    className={isActive ? 'hero-btn-primary' : 'hero-btn-secondary'}
                    onClick={() => setSelected(m)}
                  >
                    {monthLabel(m.year, m.month)}
                  </button>
                );
              })}
            </div>
          )}

          {months.length > 0 && !selected && !loadingMonths && (
            <div style={{ padding: '0 8px 12px', opacity: 0.85 }}>
              Bitte einen Monat auswählen.
            </div>
          )}

          {!!selected && months.length > 0 && (
            <div style={{ padding: '0 8px 8px' }}>
              <div style={{ fontWeight: 900, fontSize: 14, marginBottom: 10 }}>
                Top 5: {monthLabel(selected.year, selected.month)}
              </div>

              {loadingItems ? (
                <div style={{ opacity: 0.8 }}>Lade Top-Clips…</div>
              ) : items.length === 0 ? (
                <div style={{ opacity: 0.85 }}>Keine Clips in diesem Monat.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  {items.map((s) => {
                    const id = s?.id;
                    const thumb = String(s?.thumbnailUrl || '').trim();
                    const title = String(s?.title || '(ohne Titel)');
                    const creator = String(s?.creatorName || '—');
                    const platform = String(s?.platform || '—');
                    const category = String(s?.category || '—');

                    return (
                      <div key={id} className="highlight-card" style={{ ...card, alignItems: 'stretch', display: 'grid', gap: 10 }}>
                        <button
                          type="button"
                          onClick={() => setOpenId(id)}
                          style={{
                            padding: 0,
                            border: '1px solid rgba(255,255,255,0.10)',
                            borderRadius: 12,
                            overflow: 'hidden',
                            background: 'transparent',
                            cursor: 'pointer',
                          }}
                          title="Clip ansehen"
                        >
                          {thumb ? (
                            <img
                              alt={title}
                              src={thumb}
                              style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
                            />
                          ) : (
                            <div style={{ height: 150, display: 'grid', placeItems: 'center', opacity: 0.8 }}>
                              Kein Thumbnail
                            </div>
                          )}
                        </button>

                        <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.25 }}>{title}</div>
                        <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.35 }}>
                          <div><b>Creator:</b> {creator}</div>
                          <div><b>Plattform:</b> {platform}</div>
                          <div><b>Kategorie:</b> {category}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {openItem && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpenId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'grid',
            placeItems: 'center',
            padding: 16,
            zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="highlight-card"
            style={{
              ...card,
              width: '100%',
              maxWidth: 980,
              alignItems: 'stretch',
              display: 'grid',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>
                {String(openItem?.title || '(ohne Titel)')}
              </div>
              <button type="button" className="hero-btn-secondary" onClick={() => setOpenId(null)}>Schließen</button>
            </div>

            {openVideoUrl ? (
              <video
                controls
                src={openVideoUrl}
                style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }}
              />
            ) : (
              <div style={{ opacity: 0.85, fontSize: 13 }}>Video ist nicht verfügbar.</div>
            )}

            <div style={{ fontSize: 13, opacity: 0.9, display: 'grid', gap: 4 }}>
              <div><b>Creator:</b> {String(openItem?.creatorName || '—')}</div>
              <div><b>Plattform:</b> {String(openItem?.platform || '—')}</div>
              <div><b>Kategorie:</b> {String(openItem?.category || '—')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
